'use client';

import React, { useRef, useEffect, useState, useCallback, memo } from 'react';
import { CanvasAnimationData } from '../types';
import { colors } from '@/constant/colors';
import { Loader } from 'lucide-react';
import useAudioStore from '@/store/useAudioStore';

interface CanvasPlayerMiniProps {
  canvasData: CanvasAnimationData;
  loop?: boolean;
  autoPlay?: boolean;
  enableClickSound?: boolean;  // 是否启用点击播放声音，默认 true
  onClick?: () => void;
  onPlayAnimationComplete?: () => void;  // 动画播放完成回调
  className?: string;
  onError?: () => void;  // 加载失败回调
}

/**
 * CanvasPlayerMini - 简化版 Canvas 动画播放器
 * 专为 Minipet 场景设计，支持点击播放动画和声音
 */
const CanvasPlayerMini: React.FC<CanvasPlayerMiniProps> = ({
  canvasData,
  loop = true,
  autoPlay = false,  // 默认不自动播放
  enableClickSound = true,  // 默认启用点击播放声音
  onClick,
  onPlayAnimationComplete,
  className = '',
  onError
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [loadedImages, setLoadedImages] = useState<HTMLImageElement[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [actualDimensions, setActualDimensions] = useState({ width: 512, height: 512 });
  const animationIdRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number>(0);

  // 音频管理
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const { isMuted } = useAudioStore();

  // Use refs for callbacks to avoid re-triggering effects
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // 预加载帧 - 支持两种格式：bundleUrl（新）或 frames 数组（旧）
  useEffect(() => {
    let isCancelled = false;
    setLoadedImages([]);
    setLoadError(false);

    // 新格式：从 JSON bundle 加载（单次请求）
    if (canvasData.bundleUrl) {
      (async () => {
        try {
          const response = await fetch(canvasData.bundleUrl!);
          if (!response.ok) throw new Error(`Failed to fetch bundle: ${response.status}`);

          const bundle = await response.json();
          if (isCancelled) return;

          const { frames: framesBase64, mimeType } = bundle;
          if (!framesBase64 || framesBase64.length === 0) {
            throw new Error('Bundle contains no frames');
          }

          // 将 base64 转换为 Image 对象
          const images: HTMLImageElement[] = [];
          for (let i = 0; i < framesBase64.length; i++) {
            if (isCancelled) return;

            const img = await new Promise<HTMLImageElement>((resolve, reject) => {
              const image = new Image();
              image.onload = () => resolve(image);
              image.onerror = () => reject(new Error(`Failed to load frame ${i}`));
              image.src = `data:${mimeType};base64,${framesBase64[i]}`;
            });

            images.push(img);
            if (i === 0 && img.naturalWidth && img.naturalHeight) {
              setActualDimensions({ width: img.naturalWidth, height: img.naturalHeight });
            }
          }

          if (!isCancelled) {
            console.log(`✅ Loaded ${images.length} frames from bundle`);
            setLoadedImages(images);
          }
        } catch (error) {
          console.error('Failed to load from bundle:', error);
          if (!isCancelled) {
            setLoadError(true);
            onErrorRef.current?.();
          }
        }
      })();

      return () => { isCancelled = true; };
    }

    // 旧格式：从 frames 数组加载（多次请求，分批）
    const frames = canvasData.frames;
    if (!frames || frames.length === 0) {
      setLoadError(true);
      onErrorRef.current?.();
      return;
    }

    const images: HTMLImageElement[] = new Array(frames.length);
    let loadedCount = 0;
    let failedCount = 0;
    const BATCH_SIZE = 8;

    const loadBatch = async (startIndex: number) => {
      if (isCancelled) return;

      const endIndex = Math.min(startIndex + BATCH_SIZE, frames.length);
      const batchPromises: Promise<void>[] = [];

      for (let i = startIndex; i < endIndex; i++) {
        const frame = frames[i];
        if (!frame) {
          failedCount++;
          continue;
        }
        const frameUrl = typeof frame === 'string' ? frame : frame.url;
        if (!frameUrl) {
          failedCount++;
          continue;
        }

        const promise = new Promise<void>((resolve) => {
          const img = new Image();

          img.onload = () => {
            if (isCancelled) { resolve(); return; }
            images[i] = img;
            loadedCount++;
            if (i === 0 && img.naturalWidth && img.naturalHeight) {
              setActualDimensions({ width: img.naturalWidth, height: img.naturalHeight });
            }
            resolve();
          };

          img.onerror = () => {
            if (isCancelled) { resolve(); return; }
            failedCount++;
            resolve();
          };

          img.src = frameUrl;
        });

        batchPromises.push(promise);
      }

      await Promise.all(batchPromises);

      if (!isCancelled && endIndex < frames.length) {
        await loadBatch(endIndex);
      } else if (!isCancelled) {
        if (failedCount > frames.length * 0.5) {
          setLoadError(true);
          onErrorRef.current?.();
        } else {
          console.log(`✅ Loaded ${loadedCount} frames from URLs (legacy)`);
          setLoadedImages([...images]);
        }
      }
    };

    loadBatch(0);
    return () => { isCancelled = true; };
  }, [canvasData.sessionId, canvasData.frames, canvasData.bundleUrl]);

  // 绘制当前帧
  const drawFrame = useCallback((index: number) => {
    const canvas = canvasRef.current;
    if (!canvas || loadedImages.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = loadedImages[index];
    if (!img) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  }, [loadedImages]);

  // 播放动画
  useEffect(() => {
    if (!isPlaying || loadedImages.length === 0 || loadError) {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      return;
    }

    const frameInterval = 1000 / canvasData.fps;

    const animate = (now: number) => {
      if (!lastFrameTimeRef.current) {
        lastFrameTimeRef.current = now;
      }

      if (now - lastFrameTimeRef.current >= frameInterval) {
        let nextFrame = currentFrame + 1;

        if (nextFrame >= loadedImages.length) {
          if (loop) {
            nextFrame = 0;
          } else {
            // 动画播放完成
            setIsPlaying(false);
            setCurrentFrame(0); // 重置到第一帧
            drawFrame(0);
            onPlayAnimationComplete?.();
            return;
          }
        }

        drawFrame(nextFrame);
        setCurrentFrame(nextFrame);
        lastFrameTimeRef.current = now;
      }

      animationIdRef.current = requestAnimationFrame(animate);
    };

    animationIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [isPlaying, currentFrame, loadedImages, canvasData.fps, loop, drawFrame, loadError, onPlayAnimationComplete]);

  // 初始化时绘制第一帧
  useEffect(() => {
    if (loadedImages.length > 0) {
      drawFrame(0);
    }
  }, [loadedImages, drawFrame]);

  // AutoPlay 音频同步：当帧加载完成且 autoPlay=true 时自动播放音频
  useEffect(() => {
    if (autoPlay && loadedImages.length > 0 && canvasData.hasAudio && canvasData.audioUrl && !isMuted && !isAudioPlaying) {
      // 停止之前的音频
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }

      try {
        setIsAudioPlaying(true);
        audioRef.current = new Audio(canvasData.audioUrl);
        audioRef.current.volume = 0.6;
        audioRef.current.loop = loop; // 使用与动画相同的循环设置

        audioRef.current.addEventListener('ended', () => {
          setIsAudioPlaying(false);
          if (audioRef.current) {
            audioRef.current = null;
          }
        });

        audioRef.current.play().catch((error) => {
          console.error('AutoPlay audio error:', error);
          setIsAudioPlaying(false);
          if (audioRef.current) {
            audioRef.current = null;
          }
        });
      } catch (error) {
        console.error('Error creating audio for autoplay:', error);
        setIsAudioPlaying(false);
      }
    }
  }, [autoPlay, loadedImages.length, canvasData.hasAudio, canvasData.audioUrl, isMuted, isAudioPlaying, loop]);

  // 清理音频资源
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      setIsAudioPlaying(false);
    };
  }, []);

  // 监听静音状态变化，静音时停止音频
  useEffect(() => {
    if (isMuted && isAudioPlaying && audioRef.current) {
      console.log('Muting canvas minipet sound due to global mute');
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
      setIsAudioPlaying(false);
    }
  }, [isMuted, isAudioPlaying]);

  // 播放音频的辅助函数
  const playAudio = useCallback(() => {
    if (!enableClickSound || !canvasData.hasAudio || !canvasData.audioUrl || isMuted) {
      return;
    }

    // 如果正在播放，不重复播放
    if (isAudioPlaying) {
      return;
    }

    // 停止之前的音频
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    try {
      setIsAudioPlaying(true);
      audioRef.current = new Audio(canvasData.audioUrl);
      audioRef.current.volume = 0.6;

      audioRef.current.addEventListener('ended', () => {
        setIsAudioPlaying(false);
        if (audioRef.current) {
          audioRef.current = null;
        }
      });

      audioRef.current.addEventListener('error', () => {
        console.error('Failed to play canvas minipet audio');
        setIsAudioPlaying(false);
        if (audioRef.current) {
          audioRef.current = null;
        }
      });

      audioRef.current.play().catch((error) => {
        console.error('Audio play error:', error);
        setIsAudioPlaying(false);
        if (audioRef.current) {
          audioRef.current = null;
        }
      });
    } catch (error) {
      console.error('Error creating audio:', error);
      setIsAudioPlaying(false);
    }
  }, [enableClickSound, canvasData.hasAudio, canvasData.audioUrl, isMuted, isAudioPlaying]);

  // 点击处理：播放动画 + 播放音频 + 调用外部回调
  const handleClick = () => {
    // 如果正在播放，不重复触发
    if (isPlaying) {
      console.log('Animation is already playing');
      return;
    }

    // 开始播放动画
    setIsPlaying(true);
    setCurrentFrame(0);
    lastFrameTimeRef.current = 0;
    console.log('🎬 Starting canvas animation');

    // 播放音频（内置）
    playAudio();

    // 调用外部回调（如果需要额外的自定义行为）
    onClick?.();
  };

  // 加载失败不显示
  if (loadError) {
    return null;
  }

  // 使用检测到的实际尺寸
  const { width: canvasWidth, height: canvasHeight } = actualDimensions;

  return (
    <button
      onClick={handleClick}
      className={`relative ${className} cursor-pointer overflow-hidden`}
      style={{ outline: 'none' }}
    >
      <canvas
        ref={canvasRef}
        width={canvasWidth}
        height={canvasHeight}
        style={{
          display: loadedImages.length > 0 ? 'block' : 'none',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
      />

      {/* 加载中显示占位 */}
      {loadedImages.length === 0 && !loadError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader className="w-6 h-6 animate-spin" style={{ color: colors.secondaryColor }} />  
        </div>
      )}
    </button>
  );
};

// Use React.memo to prevent unnecessary re-renders from callback changes
export default memo(CanvasPlayerMini, (prevProps, nextProps) => {
  // Re-render if sessionId or frames change
  if (prevProps.canvasData.sessionId !== nextProps.canvasData.sessionId) return false;
  if (prevProps.canvasData.frames !== nextProps.canvasData.frames) return false;
  if (prevProps.canvasData.audioUrl !== nextProps.canvasData.audioUrl) return false;
  if (prevProps.canvasData.hasAudio !== nextProps.canvasData.hasAudio) return false;
  if (prevProps.loop !== nextProps.loop) return false;
  if (prevProps.autoPlay !== nextProps.autoPlay) return false;
  if (prevProps.enableClickSound !== nextProps.enableClickSound) return false;
  if (prevProps.className !== nextProps.className) return false;
  // Ignore callback changes (onClick, onPlayAnimationComplete, onError)
  return true;
});
